import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles, CornerDownLeft, X, ArrowRight } from 'lucide-react';
import { qaApi } from '@/lib/qa';
import { MarkdownText } from '@/components/MarkdownText';
import { useToast, errMessage } from '@/components/Toast';

/**
 * "Ask CadencEZ" — a global ⌘K / Ctrl+K copilot available on every app page
 * (W2-3). Context-aware of the current workspace; routes the question through
 * the existing grounded QA endpoint and renders the cited answer via the
 * editorial MarkdownText. A persistent affordance lives in the app header.
 */
export function CommandPalette({ wsId }: { wsId: string | undefined }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const ask = useMutation({
    mutationFn: (question: string) => qaApi.ask(wsId!, { agentKind: 'qa', question }),
    onError: (e) => toast.error(errMessage(e, t('copilot.error', { defaultValue: 'Could not answer that — try again.' }))),
  });

  // Global ⌘K / Ctrl+K to open, Escape to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  if (!wsId) return null;

  const result = ask.data;
  const submit = () => { if (q.trim() && !ask.isPending) ask.mutate(q.trim()); };

  return (
    <>
      {/* Header affordance */}
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-[12px] text-muted-foreground hover:text-foreground hover:border-emerald-300 transition-colors"
        aria-label={t('copilot.open', { defaultValue: 'Ask CadencEZ' })}
      >
        <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
        <span className="hidden sm:inline">{t('copilot.open', { defaultValue: 'Ask CadencEZ' })}</span>
        <kbd className="hidden sm:inline rounded bg-ink-100 px-1 text-[10px] font-mono text-ink-500">⌘K</kbd>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-[12vh]" role="dialog" aria-modal="true">
          <button aria-label={t('common.cancel', { defaultValue: 'Close' })} onClick={() => setOpen(false)} className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]" />
          <div className="relative w-full max-w-2xl rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Sparkles className="h-4 w-4 text-emerald-600 shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder={t('copilot.placeholder', { defaultValue: 'Ask about your finances — “why did marketing spend jump in May?”' })}
                className="flex-1 bg-transparent py-3.5 text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
              {q && (
                <button onClick={submit} disabled={ask.isPending} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                  {ask.isPending ? t('copilot.thinking', { defaultValue: 'Thinking…' }) : <>{t('copilot.ask', { defaultValue: 'Ask' })}<CornerDownLeft className="h-3 w-3" /></>}
                </button>
              )}
              <button onClick={() => setOpen(false)} aria-label={t('common.cancel', { defaultValue: 'Close' })} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>

            <div className="max-h-[55vh] overflow-y-auto px-4 py-3">
              {ask.isPending && <p className="text-sm text-muted-foreground">{t('copilot.thinking', { defaultValue: 'Thinking…' })}</p>}
              {!ask.isPending && result && (
                <div>
                  <MarkdownText>{result.reply}</MarkdownText>
                  <div className="mt-3 flex items-center justify-between border-t border-border pt-2.5">
                    <span className="text-[11px] text-muted-foreground">
                      {result.citedFactIds.length > 0
                        ? t('qa.view_citations', { count: result.citedFactIds.length })
                        : t('copilot.grounded', { defaultValue: 'Grounded in your data' })}
                    </span>
                    <button
                      onClick={() => { setOpen(false); nav(`/app/workspace/${wsId}/qa`); }}
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700 hover:text-emerald-800"
                    >
                      {t('copilot.open_full', { defaultValue: 'Open full Q&A' })}<ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
              {!ask.isPending && !result && (
                <p className="text-[12px] text-muted-foreground">
                  {t('copilot.hint', { defaultValue: 'Answers are grounded in your workspace data and cite the exact source rows. Press Enter to ask.' })}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
